import { useEffect, useRef } from 'react';
import { TENANT_SERVICE_URL } from '@/services/apiClient';

type MailNotification = {
  type: string;
  sender?: string;
  recipients?: string[];
  subject?: string;
  messageId?: string;
  receivedAt?: string;
};

type CalendarNotification = {
  type: string;
  recipientEmail: string;
  eventId: string;
  summary?: string;
  start?: string;
  end?: string;
};

type NotificationHandlers = {
  onMail?: (payload: MailNotification) => void;
  onCalendar?: (payload: CalendarNotification) => void;
};

const STREAM_PATH = '/api/me/notifications/stream';

function parseEventBlock(raw: string) {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }
  const data = dataLines.join('\n');
  return { eventName, data };
}

export function useNotificationStream(token: string | null, handlers: NotificationHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!token) return;
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();
    const url = `${TENANT_SERVICE_URL}${STREAM_PATH}`;

    const scheduleReconnect = (delayMs: number) => {
      if (stopped || reconnectTimer !== null) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (!stopped) connect();
      }, delayMs);
    };

    const connect = async () => {
      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!stopped) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx: number;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const raw = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            if (!raw.trim()) continue;
            const { eventName, data } = parseEventBlock(raw);
            if (!data) continue;
            try {
              const payload = JSON.parse(data);
              if (eventName.startsWith('mail.')) {
                handlersRef.current.onMail?.(payload as MailNotification);
              } else if (eventName.startsWith('calendar.')) {
                handlersRef.current.onCalendar?.(payload as CalendarNotification);
              }
            } catch {
              // ignore parse errors
            }
          }
        }
        // Stream ended cleanly (server-side SSE timeout, restart, etc.).
        // Reconnect quickly so notifications resume; the server caps emitter
        // lifetime to bound stale-session memory usage.
        scheduleReconnect(500);
      } catch {
        scheduleReconnect(3000);
      }
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      controller.abort();
    };
  }, [token]);
}
